/* Patırtı ana sayfa (Faz B1): sonsuz ürün akışı + SEO "Devamını Oku". Bağımlılık yok. */
(function () {
  'use strict';

  const toInt = (value, fallback) => {
    const number = parseInt(value, 10);
    return Number.isFinite(number) && number > 0 ? number : fallback;
  };
  const LOAD_MARGIN = 800;
  const TIMEOUT_MS = 15000;
  const AUTO_RETRY_MS = 4000;
  const MAX_CHAIN = 20;

  /*
   * <products-feed> — koleksiyonun sonraki sayfalarını Section Rendering API ile ekler.
   * İstek: <data-base-url>?section_id=<id>&page=<n>. Sunucunun döndürdüğü data-current-page /
   * data-pages değerleri esas alınır; ürünler data-product-id ile tekilleştirilir.
   * Kilit: aynı anda tek istek. Hata: 1 otomatik yeniden deneme, sonra "Yeniden dene" butonu.
   * Test için: element.dataset.loaded / dataset.page / dataset.state ve 'feed:loaded' olayı.
   */
  class ProductsFeed extends HTMLElement {
    connectedCallback() {
      if (this.ready) return;
      this.ready = true;
      this.grid = this.querySelector('[data-feed-grid]');
      this.sentinel = this.querySelector('[data-feed-sentinel]');
      this.statusNode = this.querySelector('[data-feed-status]');
      this.loadingNode = this.querySelector('[data-feed-loading]');
      this.errorNode = this.querySelector('[data-feed-error]');
      this.moreLink = this.querySelector('[data-feed-more]');
      if (!this.grid) return;
      this.page = toInt(this.dataset.currentPage, 1);
      this.pages = toInt(this.dataset.pages, 1);
      this.total = toInt(this.dataset.total, 0);
      this.loading = false;
      this.failures = 0;
      this.chain = 0;
      this.done = this.page >= this.pages;
      this.seen = new Set();
      this.items().forEach(node => { if (node.dataset.productId) this.seen.add(node.dataset.productId); });
      this.onRetry = () => this.load(true);
      this.onMore = event => { event.preventDefault(); this.load(true); };
      const retry = this.querySelector('[data-feed-retry]');
      if (retry) retry.addEventListener('click', this.onRetry);
      this.retryButton = retry;
      if (this.done) { this.finish(); return; }
      if ('IntersectionObserver' in window && this.sentinel) {
        this.observer = new IntersectionObserver(entries => {
          if (entries.some(entry => entry.isIntersecting)) { this.chain = 0; this.load(false); }
        }, { rootMargin: '0px 0px ' + LOAD_MARGIN + 'px 0px' });
        this.observer.observe(this.sentinel);
      }
      // "Daha fazla" bağlantısı JS ile de çalışır; gözlemci yoksa tek yükleme yolu budur.
      if (this.moreLink) this.moreLink.addEventListener('click', this.onMore);
      this.sync();
    }

    disconnectedCallback() {
      this.ready = false;
      if (this.observer) this.observer.disconnect();
      if (this.controller) this.controller.abort();
      clearTimeout(this.retryTimer);
      if (this.retryButton) this.retryButton.removeEventListener('click', this.onRetry);
      if (this.moreLink) this.moreLink.removeEventListener('click', this.onMore);
    }

    items() { return Array.from(this.grid.querySelectorAll('[data-feed-item]')); }

    requestUrl(page) {
      const url = new URL(this.dataset.baseUrl || '/', window.location.origin);
      url.searchParams.set('section_id', this.dataset.sectionId);
      url.searchParams.set('page', String(page));
      return url;
    }

    async load(manual) {
      if (this.loading || this.done || !this.ready) return;
      if (!manual && this.failures >= 2) return;
      if (manual) this.failures = Math.min(this.failures, 1);
      clearTimeout(this.retryTimer);
      this.loading = true;
      this.setState('loading');
      const target = this.page + 1;
      const controller = this.controller = new AbortController();
      const timer = setTimeout(() => controller.abort(), TIMEOUT_MS);
      let ok = false;
      try {
        const response = await fetch(this.requestUrl(target).href, {
          credentials: 'same-origin', signal: controller.signal, headers: { Accept: 'text/html' }
        });
        if (!response.ok) throw new Error('HTTP ' + response.status);
        const html = await response.text();
        const doc = new DOMParser().parseFromString(html, 'text/html');
        const feed = doc.querySelector('products-feed');
        if (!feed) throw new Error('feed-missing');
        const fresh = Array.from(feed.querySelectorAll('[data-feed-item]'));
        const fragment = document.createDocumentFragment();
        let added = 0;
        fresh.forEach(node => {
          const id = node.dataset.productId;
          if (!id || this.seen.has(id)) return;
          this.seen.add(id);
          fragment.appendChild(document.importNode(node, true));
          added++;
        });
        if (!this.ready) return;
        this.grid.appendChild(fragment);
        this.page = Math.max(target, toInt(feed.dataset.currentPage, target));
        this.pages = toInt(feed.dataset.pages, this.pages);
        this.total = toInt(feed.dataset.total, this.total);
        this.failures = 0;
        if (this.page >= this.pages || fresh.length === 0) this.done = true;
        ok = true;
        this.dispatchEvent(new CustomEvent('feed:loaded', {
          bubbles: true,
          detail: { page: this.page, pages: this.pages, added, loaded: this.items().length, total: this.total }
        }));
        document.dispatchEvent(new CustomEvent('collection:reloaded', { detail: { source: 'products-feed' } }));
      } catch (error) {
        if (!this.ready) return;
        this.failures++;
        this.setState('error');
        if (this.failures === 1) this.retryTimer = setTimeout(() => this.load(false), AUTO_RETRY_MS);
      } finally {
        clearTimeout(timer);
        this.loading = false;
        if (this.ready && ok) {
          this.sync();
          if (this.done) this.finish();
          else this.recheck();
        }
      }
    }

    /* Yüklenen sayfa kısa kaldıysa ve gözcü hâlâ ekrandaysa gözlemci yeniden tetiklenmez;
       burada elle kontrol edilir (bilinen "16 üründen sonra durma" sorununun çözümü). */
    recheck() {
      if (this.chain >= MAX_CHAIN) return;
      window.requestAnimationFrame(() => {
        if (!this.sentinel || this.done || this.loading || !this.ready) return;
        const box = this.sentinel.getBoundingClientRect();
        if (box.top < window.innerHeight + LOAD_MARGIN && box.bottom > -LOAD_MARGIN) {
          this.chain++;
          this.load(false);
        }
      });
    }

    setState(state) {
      this.dataset.state = state;
      const isLoading = state === 'loading';
      if (isLoading) this.setAttribute('aria-busy', 'true'); else this.removeAttribute('aria-busy');
      if (this.loadingNode) this.loadingNode.hidden = !isLoading;
      if (this.errorNode) this.errorNode.hidden = state !== 'error';
      if (this.moreLink) this.moreLink.hidden = isLoading || this.done;
    }

    sync() {
      const loaded = this.items().length;
      this.dataset.loaded = String(loaded);
      this.dataset.page = String(this.page);
      this.setState(this.done ? 'done' : 'idle');
      if (this.moreLink && !this.done) {
        const next = new URL(this.moreLink.getAttribute('href') || '/', window.location.origin);
        next.searchParams.set('page', String(this.page + 1));
        this.moreLink.href = next.pathname + next.search;
      }
      if (this.statusNode && this.dataset.countTemplate && this.total > 0) {
        this.statusNode.textContent = this.dataset.countTemplate
          .replace('__LOADED__', loaded.toLocaleString('tr-TR'))
          .replace('__TOTAL__', this.total.toLocaleString('tr-TR'));
      }
    }

    finish() {
      this.done = true;
      if (this.observer) { this.observer.disconnect(); this.observer = null; }
      this.setState('done');
      if (this.moreLink) this.moreLink.hidden = true;
      if (this.statusNode && this.dataset.endText && this.items().length > 0 && this.pages > 1) {
        this.statusNode.textContent = this.dataset.endText;
      }
    }
  }

  /* <seo-readmore> — uzun SEO metnini katlar; kısa metinde buton görünmez. */
  class SeoReadmore extends HTMLElement {
    connectedCallback() {
      if (this.ready) return;
      this.text = this.querySelector('[data-seo-text]');
      this.button = this.querySelector('[data-seo-toggle]');
      if (!this.text || !this.button) return;
      this.ready = true;
      this.label = this.button.querySelector('[data-seo-label]') || this.button;
      this.onClick = () => this.toggle();
      this.button.addEventListener('click', this.onClick);
      this.measure();
    }
    disconnectedCallback() {
      this.ready = false;
      if (this.button) this.button.removeEventListener('click', this.onClick);
    }
    measure() {
      const limit = parseInt(getComputedStyle(this).getPropertyValue('--collapsed'), 10) || 100;
      const collapsible = this.text.scrollHeight > limit + 24;
      this.classList.toggle('is-collapsible', collapsible);
      this.button.hidden = !collapsible;
    }
    toggle() {
      const expanded = !this.classList.contains('is-expanded');
      this.classList.toggle('is-expanded', expanded);
      this.button.setAttribute('aria-expanded', String(expanded));
      this.label.textContent = expanded ? this.button.dataset.less : this.button.dataset.more;
    }
  }

  if (!customElements.get('products-feed')) customElements.define('products-feed', ProductsFeed);
  if (!customElements.get('seo-readmore')) customElements.define('seo-readmore', SeoReadmore);
})();
